import { useState } from "react";
import styled from "styled-components";
import { Spacer } from "./Spacer";
import { ModalDS } from "./Modal";
import { IconCircle } from "./IconCircle";
import { BrokenImageOutlined, DeleteOutlined } from "../materialUi/materialUi";
import { colors } from "../../styles/figmaColors";

export interface PictureViewerDSProps {
  url: string;
  bottomContent?: JSX.Element;
  icon?: JSX.Element;
  onDelete?: () => void;
}

export const PictureViewerDS = (props: PictureViewerDSProps) => {
  const [zoomed, setZoomed] = useState<boolean>(false);
  const [isPictureUnreachable, setIsPictureUnreachable] =
    useState<boolean>(false);

  const canZoom =
    !props.onDelete && (!isPictureUnreachable || !!props.bottomContent);

  return (
    <StyledThumbnail>
      {isPictureUnreachable ? (
        <StyledUnreachablePicture
          $clickable={canZoom}
          onClick={canZoom ? () => setZoomed(true) : undefined}
        >
          <BrokenImageOutlined />
        </StyledUnreachablePicture>
      ) : (
        <>
          <StyledImg
            src={props.url}
            alt=""
            $clickable={canZoom}
            onClick={canZoom ? () => setZoomed(true) : undefined}
            onError={() => setIsPictureUnreachable(true)}
          />
          {props.icon && <StyledIcon>{props.icon}</StyledIcon>}
        </>
      )}
      {props.onDelete && (
        <StyledDelete onClick={props.onDelete}>
          <IconCircle
            icon={<DeleteOutlined />}
            iconColor={"errorNormal"}
            backgroundColor={"textWhite"}
            size={"S"}
          />
        </StyledDelete>
      )}
      <ModalDS visible={zoomed} onClose={() => setZoomed(false)} width={50}>
        <StyledZoom>
          <Spacer y={1} />
          {isPictureUnreachable ? (
            <StyledUnreachableZoom>
              <BrokenImageOutlined />
            </StyledUnreachableZoom>
          ) : (
            <StyledZoomImg src={props.url} alt="" />
          )}
          {props.bottomContent && (
            <>
              <Spacer y={1} />
              {props.bottomContent}
            </>
          )}
        </StyledZoom>
      </ModalDS>
    </StyledThumbnail>
  );
};

const StyledUnreachablePicture = styled.div<{ $clickable?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: ${colors["colors/surfaces/background/background_level2"]};
  ${({ $clickable }) => $clickable && "cursor: pointer;"}
  & svg {
    fill: ${colors["colors/text/lightGrey"]};
  }
`;

const StyledUnreachableZoom = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  & svg {
    width: 4rem;
    height: 4rem;
    fill: ${colors["colors/text/lightGrey"]};
  }
`;

const StyledThumbnail = styled.div`
  position: relative;
  aspect-ratio: 1;
`;

const StyledImg = styled.img<{ $clickable?: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.5rem;
  ${({ $clickable }) => $clickable && "cursor: pointer;"}
`;

const StyledDelete = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  cursor: pointer;
  & svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StyledIcon = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const StyledZoom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledZoomImg = styled.img`
  max-width: 100%;
  max-height: 70vh;
`;
